import React from 'react'

import { Link, useNavigate } from 'react-router-dom';
import Pagination from './Pagination.jsx';
import RatingModal from './RatingModal.jsx';
import BasketQuoteItemsModal from './BasketQuoteItemsModal.jsx';
import ConfirmationModal from './ConfirmationModal.jsx';
import { openExternalLink } from '../utils/shared.js';

export default function BuyerDashboard({ user, onViewProductById, onSwitchRole, onLogout }) {
    const navigate = useNavigate();
    // State for stats, orders, loading, and errors
    const [stats, setStats] = React.useState({ totalPurchases: 0, totalSpent: 0, pendingOrders: 0 });
    const [orders, setOrders] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [priceRequests, setPriceRequests] = React.useState([]);
    const [loadingPriceRequests, setLoadingPriceRequests] = React.useState(true);
    const [priceRequestsError, setPriceRequestsError] = React.useState(null);
    const [quoteRequests, setQuoteRequests] = React.useState([]);
    const [loadingQuoteRequests, setLoadingQuoteRequests] = React.useState(true);
    const [quoteRequestsError, setQuoteRequestsError] = React.useState(null);
    const [rebuildRequests, setRebuildRequests] = React.useState([]);
    const [loadingRebuildRequests, setLoadingRebuildRequests] = React.useState(true);
    const [rebuildRequestsError, setRebuildRequestsError] = React.useState(null);
    const [actionMessage, setActionMessage] = React.useState(null);
    const [orderToRate, setOrderToRate] = React.useState(null); // State for the rating modal
    const [showBasketModal, setShowBasketModal] = React.useState(false); // State for basket items modal
    const [basketItems, setBasketItems] = React.useState([]); // State for basket items
    const [loadingBasketItems, setLoadingBasketItems] = React.useState(false); // State for loading basket items
    const [showDeleteModal, setShowDeleteModal] = React.useState(false); // State for delete confirmation modal
    const [itemToDelete, setItemToDelete] = React.useState(null); // State for item to delete

    // --- NEW: Pagination State ---
    const [ordersPage, setOrdersPage] = React.useState(1);
    const [priceRequestsPage, setPriceRequestsPage] = React.useState(1);
    const [quoteRequestsPage, setQuoteRequestsPage] = React.useState(1);
    const [rebuildRequestsPage, setRebuildRequestsPage] = React.useState(1);
    const itemsPerPage = 10; // View limit of 10 per section

    // --- NEW: Handle anchor scrolling for bottom navigation ---
    React.useEffect(() => {
        const hash = window.location.hash;
        if (hash) {
            // Remove of # and find of element
            const elementId = hash.substring(1);
            const element = document.getElementById(elementId);
            
            if (element) {
                // Small delay to ensure content is rendered
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
            }
        }
    }, []); // Run once when component mounts
    // This utility is defined locally to make the component more robust and self-contained.
    const formatPrice = (price) => {
        const num = parseFloat(price);
        if (isNaN(num)) {
            return 'R 0.00';
        }
        return `R ${num.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // Combined data fetching function for stats and orders
    const fetchData = React.useCallback(async () => {
        if (user && user.id) {
            setLoading(true);
            setError(null);
            setLoadingPriceRequests(true);
            setPriceRequestsError(null);
            setLoadingQuoteRequests(true);
            setQuoteRequestsError(null);
            setLoadingRebuildRequests(true);
            setRebuildRequestsError(null);
            try {
                // Fetch stats and orders in parallel for better performance
                const [statsResponse, ordersResponse, priceRequestsResponse, quoteRequestsResponse, rebuildRequestsResponse] = await Promise.all([
                    fetch(`/api/buyer_stats.php?buyer_id=${user.id}`, { credentials: 'include' }),
                    fetch(`/api/buyer_orders.php?buyer_id=${user.id}`, { credentials: 'include' }),
                    fetch(`/api/buyer_price_requests.php`, { credentials: 'include' }), // Fetches for the logged-in user
                    fetch(`/api/buyer_get_quote_requests.php`, { credentials: 'include' }),
                    fetch(`/api/buyer_rebuild_requests.php`, { credentials: 'include' })
                ]);

                if (!statsResponse.ok) throw new Error('Could not fetch dashboard stats.');
                if (!ordersResponse.ok) throw new Error('Could not fetch order history.');
                if (!priceRequestsResponse.ok) throw new Error('Could not fetch price requests.');
                if (!quoteRequestsResponse.ok) throw new Error('Could not fetch quote requests.');
                if (!rebuildRequestsResponse.ok) throw new Error('Could not fetch rebuild requests.');

                const statsData = await statsResponse.json();
                const ordersData = await ordersResponse.json();
                const priceRequestsData = await priceRequestsResponse.json();
                const quoteRequestsData = await quoteRequestsResponse.json();
                const rebuildRequestsData = await rebuildRequestsResponse.json();

                setStats(statsData);
                setOrders(ordersData);
                setPriceRequests(priceRequestsData);
                setQuoteRequests(quoteRequestsData);
                setRebuildRequests(rebuildRequestsData);

            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
                setLoadingPriceRequests(false);
                setLoadingQuoteRequests(false);
                setLoadingRebuildRequests(false);
            }
        }
    }, [user.id]);

    // Fetch data on component mount or when user changes
    React.useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Handler for accepting delivery
    const handleAcceptDelivery = async (orderId, allocationId) => {
        setActionMessage(null);
        setOrders(prevOrders => prevOrders.map(order => 
            order.id === orderId ? { ...order, isProcessing: true } : order
        ));

        try {
            const response = await fetch('/api/accept_delivery.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ order_id: orderId, allocation_id: allocationId })
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.error || 'Failed to accept delivery.');
            
            setActionMessage({ type: 'success', text: result.message });
            fetchData(); // Refresh both stats and orders list
        } catch (err) {
            setActionMessage({ type: 'error', text: err.message });
            // Reset processing state on error
            setOrders(prevOrders => prevOrders.map(order => 
                order.id === orderId ? { ...order, isProcessing: false } : order
            ));
        }
    };

    const handleCloseRatingModal = (didSubmit) => {
        setOrderToRate(null);
        if (didSubmit) {
            fetchData(); // Refresh data if a rating was submitted
        }
    };

    const handleViewQuote = (req) => {
        const url = `/api/view_quote.php?product_id=${req.product_id}&listing_type=${req.listing_type}`;
        openExternalLink(url);
    };

    const handleViewProduct = async (productId, listingType, requestId) => {
        // Basket quotes don't have single products - show modal with items
        if (listingType === 'basket' || productId === 0) {
            setShowBasketModal(true);
            setLoadingBasketItems(true);
            try {
                const response = await fetch(`/api/get_basket_quote_items.php?quote_request_id=${requestId}`, { credentials: 'include' });
                if (!response.ok) {
                    const errData = await response.json();
                    throw new Error(errData.error || 'Failed to fetch basket items.');
                }
                const items = await response.json();
                setBasketItems(items);
            } catch (error) {
                console.error('Error fetching basket items:', error);
                alert(`Error loading basket items: ${error.message}`);
                setShowBasketModal(false);
            } finally {
                setLoadingBasketItems(false);
            }
            return;
        }
        onViewProductById(productId, listingType);
    };

    const handleDeleteQuote = async (req) => {
        setItemToDelete(req);
        setShowDeleteModal(true);
    };

    const confirmDeleteQuote = async () => {
        if (!itemToDelete) return;
        
        try {
            const response = await fetch('/api/delete_quote_request.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ request_id: itemToDelete.request_id })
            });
            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Failed to delete quote request.');
            }
            setQuoteRequests(quoteRequests.filter(qr => qr.request_id !== itemToDelete.request_id));
            setActionMessage({ type: 'success', text: 'Quote request deleted successfully' });
        } catch (error) {
            console.error('Error deleting quote request:', error);
            setActionMessage({ type: 'error', text: 'Failed to delete quote request' });
        } finally {
            setShowDeleteModal(false);
            setItemToDelete(null);
        }
    };

    // --- Pagination Logic for all sections ---
    const totalOrdersPages = Math.ceil(orders.length / itemsPerPage);
    const currentOrders = orders.slice((ordersPage - 1) * itemsPerPage, ordersPage * itemsPerPage);

    const totalPriceRequestPages = Math.ceil(priceRequests.length / itemsPerPage);
    const currentPriceRequests = priceRequests.slice((priceRequestsPage - 1) * itemsPerPage, priceRequestsPage * itemsPerPage);

    const totalQuotePages = Math.ceil(quoteRequests.length / itemsPerPage);
    const currentQuoteRequests = quoteRequests.slice((quoteRequestsPage - 1) * itemsPerPage, quoteRequestsPage * itemsPerPage);

    const totalRebuildRequestPages = Math.ceil(rebuildRequests.length / itemsPerPage);
    const currentRebuildRequests = rebuildRequests.slice((rebuildRequestsPage - 1) * itemsPerPage, rebuildRequestsPage * itemsPerPage);

    if (loading) {
        return <div className="text-center py-10"><i className="fas fa-spinner fa-spin text-4xl text-blue-400"></i></div>;
    }

    if (error) {
        return <div className="text-center py-10 text-red-500">Error: {error}</div>;
    }

    return (
        <div data-name="buyer-dashboard" data-file="components/BuyerDashboard.js" className="container mx-auto px-4 py-8">
            <div className="mb-8">
                <h1 className="text-4xl md:text-6xl font-black text-white tracking-tighter uppercase">Buyer Dashboard</h1>
                <p className="text-blue-400 font-bold uppercase tracking-[0.3em] text-xs mt-2">Orders, Quotes & Price Requests</p>
                
                {/* Role Switching and Profile Buttons */}
                <div className="flex flex-wrap gap-3 mt-6">
                    {/* Profile Button */}
                    <button 
                        onClick={() => navigate('/profile')}
                        className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors text-sm flex items-center backdrop-blur-sm border border-white/20"
                    >
                        <i className="fas fa-user-circle mr-2"></i>
                        My Profile
                    </button>
                    
                    {/* Role Switch Buttons */}
                    {user && user.roles && user.roles.length > 1 && (
                        <>
                            {/* Buyer Button (Active) */}
                            <button 
                                onClick={() => user.activeRole !== 'buyer' && onSwitchRole('buyer')}
                                className={`px-4 py-2 rounded-lg transition-colors text-sm flex items-center backdrop-blur-sm border ${
                                    user.activeRole === 'buyer' 
                                        ? 'bg-blue-600/80 hover:bg-blue-700/80 text-white border-blue-400/30' 
                                        : 'bg-gray-500/30 hover:bg-gray-500/40 text-gray-400 border-gray-500/20 cursor-not-allowed'
                                }`}
                                disabled={user.activeRole === 'buyer'}
                            >
                                <i className="fas fa-shopping-cart mr-2"></i>
                                Buyer
                            </button>
                            
                            {/* Seller Button */}
                            <button 
                                onClick={() => user.activeRole !== 'seller' && onSwitchRole('seller')}
                                className={`px-4 py-2 rounded-lg transition-colors text-sm flex items-center backdrop-blur-sm border ${
                                    user.activeRole === 'seller' 
                                        ? 'bg-blue-600/80 hover:bg-blue-700/80 text-white border-blue-400/30' 
                                        : 'bg-gray-500/30 hover:bg-gray-500/40 text-gray-400 border-gray-500/20'
                                }`}
                                disabled={user.activeRole === 'seller'}
                            >
                                <i className="fas fa-store mr-2"></i>
                                Seller
                            </button>
                        </>
                    )}
                </div>
            </div>
            
            {/* Stats Cards */}
            <div id="stats-overview" className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow-lg p-6">
                    <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2">Total Purchases</h3>
                    <p className="text-3xl font-bold text-blue-600">{stats.totalPurchases}</p>
                </div>
                <div className="bg-white rounded-xl shadow-lg p-6">
                    <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2">Total Spent</h3>
                    <p className="text-3xl font-bold text-green-600">{formatPrice(stats.totalSpent)}</p>
                </div>
                <div className="bg-white rounded-xl shadow-lg p-6">
                    <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2">Pending Orders</h3>
                    <p className="text-3xl font-bold text-amber-600">{stats.pendingOrders}</p>
                </div>
            </div>
            
            {actionMessage && (
                <div className={`p-3 rounded-lg text-sm mb-4 ${actionMessage.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                    {actionMessage.text}
                </div>
            )}

            {/* --- NEW: My Price Requests Section --- */}
            <div id="price-requests" className="bg-white rounded-xl shadow-lg p-6 mt-8">
                <h2 className="text-sm font-black text-gray-400 uppercase tracking-[0.2em] mb-6">Active Price Inquiries</h2>
                {loadingPriceRequests && <div className="text-center py-4"><i className="fas fa-spinner fa-spin text-2xl text-blue-400"></i></div>}
                {priceRequestsError && <div className="text-red-500 text-center py-4">Error: {priceRequestsError}</div>}
                {!loadingPriceRequests && !priceRequestsError && priceRequests.length === 0 && (
                    <div className="text-center py-10">
                        <i className="fas fa-tags text-4xl text-gray-400 mb-4"></i>
                        <h3 className="text-xl font-semibold text-gray-700">No Price Requests Yet</h3>
                        <p className="text-gray-500">Items for which you request the price will appear here.</p>
                    </div>
                )}
                {!loadingPriceRequests && !priceRequestsError && priceRequests.length > 0 && (
                    <div className="space-y-4">
                        {currentPriceRequests.map(req => (
                            <div key={req.request_id} className="border rounded-lg p-4 flex flex-col sm:flex-row justify-between sm:items-center">
                                <div className="flex items-center mb-4 sm:mb-0">
                                    <img className="h-16 w-16 rounded-lg object-cover" src={req.image_url_1 || 'https://via.placeholder.com/150'} alt={req.product_name} />
                                    <div className="ml-4">
                                        <p className="font-semibold text-gray-900">{req.product_name}</p>
                                        <p className="text-sm text-gray-500 font-mono">{req.listing_number}</p>
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                                    {req.status === 'requested' && (
                                        <span className="px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                            Price Requested
                                        </span>
                                    )}
                                    {req.status === 'approved' && (
                                        <div className="flex items-center gap-4">
                                            <div className="text-right">
                                                <p className="text-lg font-bold text-blue-600">{formatPrice(req.price)}</p>
                                                <p className="text-xs text-gray-500">Price Revealed</p>
                                            </div>
                                            <button
                                                // Call the function passed down from App.js
                                                onClick={() => onViewProductById(req.product_id, req.listing_type)}
                                                className="primary-gradient text-white py-2 px-4 rounded-lg hover:opacity-90 transition-opacity text-sm"
                                            >
                                                <i className="fas fa-file-invoice-dollar mr-2"></i> Request Quote
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <Pagination currentPage={priceRequestsPage} totalPages={totalPriceRequestPages} onPageChange={setPriceRequestsPage} />
            </div>

            {/* --- NEW: My Quote Requests Section --- */}
            <div id="quote-requests" className="bg-white rounded-xl shadow-lg p-6 mt-8">
                <h2 className="text-sm font-black text-gray-400 uppercase tracking-[0.2em] mb-6">Official Quotations</h2>
                {loadingQuoteRequests && <div className="text-center py-4"><i className="fas fa-spinner fa-spin text-2xl text-blue-400"></i></div>}
                {quoteRequestsError && <div className="text-red-500 text-center py-4">Error: {quoteRequestsError}</div>}
                {!loadingQuoteRequests && !quoteRequestsError && quoteRequests.length === 0 && (
                    <div className="text-center py-10">
                        <i className="fas fa-file-invoice-dollar text-4xl text-gray-400 mb-4"></i>
                        <h3 className="text-xl font-semibold text-gray-700">No Quote Requests Yet</h3>
                        <p className="text-gray-500">Items for which you request a quote will appear here.</p>
                    </div>
                )}
                {!loadingQuoteRequests && !quoteRequestsError && quoteRequests.length > 0 && (
                    <div className="space-y-4">
                        {currentQuoteRequests.map(req => (
                            <div key={req.request_id} className="border rounded-lg p-4 flex flex-col sm:flex-row justify-between sm:items-center">
                                <div className="flex items-center mb-4 sm:mb-0">
                                    <img className="h-16 w-16 rounded-lg object-cover" src={req.image_url_1 || 'https://via.placeholder.com/150'} alt={req.product_name} />
                                    <div className="ml-4">
                                        <p className="font-semibold text-gray-900">{req.product_name}</p>
                                        <p className="text-sm text-gray-500 font-mono">{req.listing_number}</p>
                                        <p className="text-xs text-gray-500">Requested: {new Date(req.request_date).toLocaleDateString()}</p>
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                                    <div className="flex space-x-2">
                                        <button 
                                            onClick={() => handleViewProduct(req.product_id, req.listing_type, req.request_id)} 
                                            className="text-blue-600 hover:text-blue-900 font-medium text-sm"
                                        >
                                            View Item
                                        </button>
                                        <button onClick={() => handleViewQuote(req)} className="text-green-600 hover:text-green-900 font-medium text-sm">
                                            View Quote PDF
                                        </button>
                                        <button onClick={() => handleDeleteQuote(req)} className="text-red-600 hover:text-red-900 font-medium text-sm">
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <Pagination currentPage={quoteRequestsPage} totalPages={totalQuotePages} onPageChange={setQuoteRequestsPage} />
            </div>

            {/* --- NEW: My Rebuild Requests Section --- */}
            <div id="rebuild-requests" className="bg-white rounded-xl shadow-lg p-6 mt-8">
                <h2 className="text-sm font-black text-gray-400 uppercase tracking-[0.2em] mb-6">Rebuild Configurations</h2>
                {loadingRebuildRequests && <div className="text-center py-4"><i className="fas fa-spinner fa-spin text-2xl text-blue-400"></i></div>}
                {rebuildRequestsError && <div className="text-red-500 text-center py-4">Error: {rebuildRequestsError}</div>}
                {!loadingRebuildRequests && !rebuildRequestsError && rebuildRequests.length === 0 && (
                    <div className="text-center py-10">
                        <i className="fas fa-tools text-4xl text-gray-400 mb-4"></i>
                        <h3 className="text-xl font-semibold text-gray-700">No Rebuild Requests Yet</h3>
                        <p className="text-gray-500">Rebuild configurations you submit will appear here.</p>
                    </div>
                )}
                {!loadingRebuildRequests && !rebuildRequestsError && rebuildRequests.length > 0 && (
                    <div className="space-y-4">
                        {currentRebuildRequests.map(req => (
                            <div key={req.request_id} className="border rounded-lg p-4 flex flex-col sm:flex-row justify-between sm:items-center">
                                <div className="flex items-center mb-4 sm:mb-0">
                                    <img className="h-16 w-16 rounded-lg object-cover" src={req.image_url_1 || 'https://via.placeholder.com/150'} alt={req.product_name} />
                                    <div className="ml-4">
                                        <p className="font-semibold text-gray-900">{req.product_name}</p>
                                        <p className="text-sm text-gray-500 font-mono">{req.listing_number}</p>
                                        <p className="text-xs text-gray-500">Requested: {new Date(req.request_date).toLocaleDateString()}</p>
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                                    <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${
                                        req.status === 'requested' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                                    }`}>
                                        {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                                    </span>
                                    <button
                                        onClick={() => onViewProductById(req.listing_id, 'rebuild')}
                                        className="bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                                    >
                                        View Item
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <Pagination currentPage={rebuildRequestsPage} totalPages={totalRebuildRequestPages} onPageChange={setRebuildRequestsPage} />
            </div>

            {/* Purchase History Section */}
            <div id="purchase-history" className="bg-white rounded-xl shadow-lg p-6 mt-8">
                <h2 className="text-sm font-black text-gray-400 uppercase tracking-[0.2em] mb-6">Procurement History</h2>
                {orders.length === 0 ? (
                    <div className="text-center py-10">
                        <i className="fas fa-shopping-cart text-4xl text-gray-400 mb-4"></i>
                        <h3 className="text-xl font-semibold text-gray-700">No Purchases Yet</h3>
                        <p className="text-gray-500">Your purchased items will appear here.</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Desktop Headers */}
                        <div className="hidden md:grid md:grid-cols-10 gap-4 px-4 py-2 bg-gray-50 rounded-lg">
                            <div className="md:col-span-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Product</div>
                            <div className="md:col-span-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">Date</div>
                            <div className="md:col-span-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</div>
                            <div className="md:col-span-2 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Action</div>
                        </div>
                        {/* Orders List */}
                        {currentOrders.map(order => (
                            <div key={order.id} className="bg-white border rounded-lg p-4 grid grid-cols-2 md:grid-cols-10 gap-x-4 gap-y-2 items-center">
                                {/* Product Info */}
                                <div className="col-span-2 md:col-span-4 flex items-center">
                                    <img className="h-12 w-12 rounded-lg object-cover" src={order.product_image || 'https://via.placeholder.com/150'} alt={order.product_name} />
                                    <div className="ml-4">
                                        <div className="text-sm font-medium text-gray-900">{order.product_name}</div>
                                        <div className="text-sm text-gray-500 font-mono">{order.listing_number}</div>
                                    </div>
                                </div>
                                {/* Date */}
                                <div className="md:col-span-2">
                                    <div className="text-xs text-gray-500 md:hidden">Date</div>
                                    <div className="text-sm text-gray-500">{new Date(order.order_date).toLocaleDateString()}</div>
                                </div>
                                {/* Status */}
                                <div className="md:col-span-2">
                                    <div className="text-xs text-gray-500 md:hidden">Status</div>
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${
                                        order.status === 'paid' ? 'bg-blue-100 text-blue-800' :
                                        order.status === 'delivery_initiated' ? 'bg-yellow-100 text-yellow-800' :
                                        order.status === 'completed' ? 'bg-green-100 text-green-800' :
                                        order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                        order.status === 'dispute' ? 'bg-amber-100 text-amber-800' :
                                        'bg-gray-100 text-gray-800'
                                    }`}>{order.status.replace('_', ' ')}</span>
                                </div>
                                {/* Action Button */}
                                <div className="col-span-2 md:col-span-2 text-right">
                                    {order.status === 'cancelled' && (
                                        <span className="text-sm text-red-600">Order Cancelled</span>
                                    )}
                                    {order.status === 'dispute' && (
                                        <span className="text-sm text-amber-600">Dispute in Progress</span>
                                    )}
                                    {order.status === 'delivery_initiated' && (
                                        <button 
                                            onClick={() => handleAcceptDelivery(order.id, order.seller_allocation_id)}
                                            disabled={order.isProcessing}
                                            className="bg-green-500 text-white py-2 px-4 rounded-lg hover:bg-green-600 text-sm disabled:opacity-50"
                                        >
                                            {order.isProcessing ? 'Accepting...' : 'Accept Delivery'}
                                        </button>
                                    )}
                                    {order.status === 'paid' && (
                                        <span className="text-sm text-gray-500">Awaiting Delivery</span>
                                    )}
                                    {order.status === 'completed' && (
                                        <div className="flex flex-col items-end space-y-2">
                                            <span className="text-sm text-green-600 font-semibold">Order Completed</span>
                                            {!order.is_rated ? (
                                                <button 
                                                    onClick={() => setOrderToRate(order)}
                                                    className="bg-blue-500 text-white py-2 px-4 rounded-lg hover:bg-blue-600 text-sm"
                                                >Rate Seller</button>
                                            ) : (
                                                <span className="text-sm text-gray-500 font-semibold"><i className="fas fa-check-circle text-green-500 mr-1"></i>Rated</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <Pagination currentPage={ordersPage} totalPages={totalOrdersPages} onPageChange={setOrdersPage} />
            </div>

            {/* Rating Modal */}
            <RatingModal
                isOpen={!!orderToRate}
                onClose={handleCloseRatingModal}
                order={orderToRate}
                user={user}
            />

            {/* Basket Quote Items Modal */}
            <BasketQuoteItemsModal
                isOpen={showBasketModal}
                onClose={() => setShowBasketModal(false)}
                items={basketItems}
                onViewProduct={onViewProductById}
            />

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={confirmDeleteQuote}
                title="Delete Quote Request"
                message={`Are you sure you want to delete this quote request for "${itemToDelete?.product_name}"?`}
                confirmText="Delete"
                cancelText="Cancel"
                type="danger"
            />
        </div>
    );
}
